import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import LandingPage from './components/LandingPage';
import CourseListPage from './components/CourseListPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import Dashboard from './components/Dashboard';
import CourseDetails from './components/CourseDetails';
import PublicCourseDetails from './components/PublicCourseDetails';
import Profile from './components/Profile';
import StudentLiveSessions from './components/StudentLiveSessions';
import CourseEnrollment from './components/CourseEnrollment';
import APITest from './components/APITest';
import LoadingSpinner from './components/LoadingSpinner';
import StudentSettings from './components/StudentSettings';
import './App.css';

function AppContent() {
  const { isAuthenticated, login, logout, loading } = useAuth();

  const handleLogin = async (email, password) => {
    try {
      const result = await login(email, password);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const handleLogout = () => {
    logout();
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <LoadingSpinner size="large" message="Loading..." />
      </div>
    );
  }

  return (
    <Router>
      <div className="App min-h-screen">
        <Routes>
          <Route 
            path="/" 
            element={<LandingPage />} 
          />
          <Route 
            path="/courses" 
            element={<CourseListPage />} 
          />
          <Route 
            path="/login" 
            element={
              !isAuthenticated ? (
                <LoginPage onLogin={handleLogin} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            } 
          />
          <Route 
            path="/register" 
            element={
              !isAuthenticated ? (
                <RegisterPage onLogin={handleLogin} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                <Dashboard onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          {/* Public course details - no authentication required */}
          <Route 
            path="/course/:courseId" 
            element={<PublicCourseDetails isAuthenticated={isAuthenticated} />} 
          />
          {/* Protected course access for enrolled students */}
          <Route 
            path="/learn/:courseId" 
            element={
              isAuthenticated ? (
                <CourseDetails onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/profile" 
            element={
              isAuthenticated ? (
                <Profile onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <StudentSettings onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route 
            path="/live-sessions" 
            element={
              isAuthenticated ? (
                <StudentLiveSessions onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/enroll/:courseId" 
            element={<CourseEnrollment />} 
          />
          <Route 
            path="/api-test" 
            element={<APITest />} 
          />
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
